/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { uploadChunked } from "../SubcontractOps";
import { fmtD } from "../../lib/date";
import { Camera, Loader2, Mail, MessageSquare, Package, Building2, Truck, Pencil, Trash2, Save, Ban } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Number(n || 0).toLocaleString();

export const DEFAULT_PICKUP_DAYS = 3;
export const emptyDelivery = () => ({ method: "pickup", pickupDays: DEFAULT_PICKUP_DAYS, envelopePhoto: null, notifySms: true, notifyEmail: true, note: "", noteTouched: false });

/* 支票领取方式（必选）：自取（N 个工作日后来公司领取，默认 3，可改）/ 邮寄（信封照片可稍后补传）+ 短信 / 邮件 勾选 + 英文附言（通用模板，可编辑 / 存为模板）—— 打印弹窗与已付行「设置领取」共用 */
export function DeliveryPicker({ r, zh, api, value, onChange, testid = "delivery" }) {
  const [uploading, setUploading] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [savingTpl, setSavingTpl] = useState(false);
  const c = r.payeeContact || {};
  const set = (patch) => onChange({ ...value, ...patch });
  const valueRef = useRef(value); valueRef.current = value;
  useEffect(() => {
    api.get("/finance/delivery-notice-template").then(({ data }) => { setTpl(data); if (!valueRef.current.noteTouched && !valueRef.current.note) onChange({ ...valueRef.current, note: data.text || "" }); }).catch(() => setTpl({ text: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  const saveTpl = async () => {
    setSavingTpl(true);
    try { const { data } = await api.put("/finance/delivery-notice-template", { text: value.note }); setTpl(data); toast.success(zh ? "已存为通用附言模板（各工种共用）" : "Saved as the shared template"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSavingTpl(false);
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try { const up = await uploadChunked(api, f); set({ envelopePhoto: up }); toast.success(zh ? "信封照片已上传" : "Envelope photo uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    setUploading(false);
    e.target.value = "";
  };
  const days = Number(value.pickupDays) || DEFAULT_PICKUP_DAYS;
  const opt = (k, Icon, label, hint, extra) => (
    <label key={k} data-testid={`${testid}-method-${k}`} className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 cursor-pointer text-xs ${value.method === k ? "border-brand-500 bg-brand-50" : "border-slate-200 hover:border-slate-300"}`}>
      <input type="radio" name={`${testid}-method`} checked={value.method === k} onChange={() => set({ method: k })} className="mt-0.5" />
      <Icon className="h-3.5 w-3.5 mt-0.5 text-slate-500 shrink-0" />
      <span className="flex-1 min-w-0"><b className="text-slate-800">{label}</b>{hint && <span className="block text-[11px] text-slate-500">{hint}</span>}{extra}</span>
    </label>
  );
  const dayInput = value.method === "pickup" && (
    <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-700" onClick={(e) => e.preventDefault()}>
      {zh ? "发出后" : "Ready in"}
      <input type="number" min="1" max="30" value={value.pickupDays ?? DEFAULT_PICKUP_DAYS} onChange={(e) => set({ pickupDays: Math.max(1, Math.min(30, Number(e.target.value) || DEFAULT_PICKUP_DAYS)) })} onClick={(e) => e.stopPropagation()} className="w-14 border border-slate-300 rounded-md px-1.5 py-0.5 text-center bg-white" data-testid={`${testid}-pickup-days`} />
      {zh ? "个工作日后可领取（默认 3）" : "business days (default 3)"}
    </span>
  );
  const tplDirty = tpl && (value.note || "") !== (tpl.text || "");
  return (
    <div className="space-y-2" data-testid={`${testid}-picker`} data-method={value.method || "none"} data-days={days}>
      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Package className="h-3.5 w-3.5" />{zh ? "支票交付 · 通知分包商（必选）" : "Check delivery · notify subcontractor (required)"}</p>
      <div className="grid gap-1.5">
        {opt("pickup", Building2, zh ? `自取 · ${days} 个工作日后来公司领取` : `Pickup · ready in ${days} business day${days === 1 ? "" : "s"}`, zh ? "系统按工作日自动算出可领取日期并写进通知" : "Pickup date is computed automatically", dayInput)}
        {opt("mail", Truck, zh ? "邮寄 · 信封照片可稍后补传" : "Mail · envelope photo can be added later", zh ? "建议拍邮寄信封留证；没拍也能确认，之后在已付行补传" : "Envelope photo recommended; can be uploaded later from the paid row")}
      </div>
      {value.method === "mail" && (
        <div className="flex items-center gap-2 text-xs" data-testid={`${testid}-envelope`} data-has={value.envelopePhoto ? 1 : 0}>
          <label className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border cursor-pointer ${value.envelopePhoto ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-slate-50 text-slate-700"}`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            {value.envelopePhoto ? (zh ? "信封照片已上传 · 重拍" : "Envelope uploaded · retake") : (zh ? "拍 / 上传邮寄信封照片（可选，可后补）" : "Upload envelope photo (optional)")}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-envelope-input`} />
          </label>
          {value.envelopePhoto && <span className="text-[11px] text-slate-500 truncate max-w-[180px]">{value.envelopePhoto.filename}</span>}
        </div>
      )}
      {value.method && (
        <div className="space-y-1.5 text-xs" data-testid={`${testid}-channels`}>
          <div className="flex flex-wrap gap-3">
            <label className={`inline-flex items-center gap-1.5 ${c.phone ? "text-slate-700" : "text-slate-400"}`}>
              <input type="checkbox" checked={!!value.notifySms && !!c.phone} disabled={!c.phone} onChange={(e) => set({ notifySms: e.target.checked })} data-testid={`${testid}-sms`} />
              <MessageSquare className="h-3.5 w-3.5" />{zh ? "短信" : "SMS"}{c.phone ? ` ${c.phoneMasked}` : (zh ? "（分包商无手机号）" : " (no phone)")}
            </label>
            <label className={`inline-flex items-center gap-1.5 ${c.email ? "text-slate-700" : "text-slate-400"}`}>
              <input type="checkbox" checked={!!value.notifyEmail && !!c.email} disabled={!c.email} onChange={(e) => set({ notifyEmail: e.target.checked })} data-testid={`${testid}-email`} />
              <Mail className="h-3.5 w-3.5" />{zh ? "邮件" : "Email"}{c.email ? ` ${c.emailAddr}` : (zh ? "（分包商无邮箱）" : " (no email)")}
            </label>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500"><span>{zh ? "英文附言（通用模板 · 各工种共用，可临时改动）" : "English note (shared template, editable)"}</span>
              {tplDirty && <button type="button" onClick={saveTpl} disabled={savingTpl} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-0.5 text-indigo-700 hover:underline" data-testid={`${testid}-note-save`}>{savingTpl ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}{zh ? "存为通用模板" : "Save as template"}</button>}
              {tpl?.updatedBy && !tplDirty && <span className="ml-auto" data-testid={`${testid}-note-tpl-info`}>{zh ? `模板 · ${tpl.updatedBy}` : `Template · ${tpl.updatedBy}`}</span>}
            </div>
            <textarea value={value.note} onChange={(e) => set({ note: e.target.value, noteTouched: true })} rows={2} placeholder={zh ? "英文附言（随短信 / 邮件一起发给分包商）" : "English note sent with the SMS / email"} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1 text-xs" data-testid={`${testid}-note`} />
          </div>
        </div>
      )}
    </div>
  );
}

export const deliveryBody = (v) => (v.method ? { delivery: { method: v.method, pickupDays: Number(v.pickupDays) || DEFAULT_PICKUP_DAYS, envelopePhoto: v.envelopePhoto || undefined, notifySms: !!v.notifySms, notifyEmail: !!v.notifyEmail, note: v.note } } : {});

/* 已付行上的交付状态芯片；邮寄未传信封 → 可直接补传（需 api + onDone） */
export function DeliveryChip({ r, zh, token, api, onDone }) {
  const [up, setUp] = useState(false);
  const d = r.delivery;
  if (!d) return null;
  const sent = d.sent || {};
  const ch = [sent.sms ? (sent.sms.ok ? (zh ? "短信✓" : "SMS✓") : (zh ? "短信×" : "SMS×")) : null, sent.email ? (sent.email.ok ? (zh ? "邮件✓" : "Email✓") : (zh ? "邮件×" : "Email×")) : null].filter(Boolean).join(" ");
  const addEnvelope = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUp(true);
    try { const photo = await uploadChunked(api, f); await api.post(`/payment-requests/${r.id}/delivery/envelope`, { envelopePhoto: photo }); toast.success(zh ? "信封照片已补传" : "Envelope photo added"); onDone?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    setUp(false); e.target.value = "";
  };
  return (
    <span data-testid={`pay-delivery-${r.id}`} data-method={d.method} data-envelope={d.envelopePhoto?.fileId ? 1 : 0} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${d.method === "mail" ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800"}`} title={d.message?.en || d.message?.zh || ""}>
      {d.method === "mail" ? <Truck className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
      {d.method === "mail" ? (zh ? `已邮寄 ${fmtD(d.mailedAt)}` : `Mailed ${fmtD(d.mailedAt)}`) : (zh ? `自取 · ${fmtD(d.pickupDate)} 起` : `Pickup from ${fmtD(d.pickupDate)}`)}
      {ch && <span className="opacity-80">· {ch}</span>}
      {d.envelopePhoto?.fileId && <a href={`${BACKEND}/api/files/${d.envelopePhoto.fileId}/download?auth=${token}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="underline" data-testid={`pay-delivery-envelope-${r.id}`}>{zh ? "信封" : "envelope"}</a>}
      {d.method === "mail" && !d.envelopePhoto?.fileId && api && (
        <label onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-0.5 underline cursor-pointer text-amber-800" data-testid={`pay-delivery-envelope-add-${r.id}`}>
          {up ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}{zh ? "补传信封" : "add envelope"}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addEnvelope} data-testid={`pay-delivery-envelope-input-${r.id}`} />
        </label>
      )}
    </span>
  );
}

/* 已付后补设领取方式 / 改邮寄 */
export function DeliveryDialog({ r, zh, api, onClose, onDone }) {
  const [v, setV] = useState(emptyDelivery());
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!v.method) return toast.error(zh ? "请选择 自取 或 邮寄" : "Pick pickup or mail");
    setBusy(true);
    try {
      const { data } = await api.post(`/payment-requests/${r.id}/delivery`, deliveryBody(v));
      const s = data?.delivery?.sent || {};
      toast.success(zh ? `已记录${v.method === "mail" ? "邮寄" : "自取"}${s.sms?.ok || s.email?.ok ? " · 通知已发送" : ""}` : "Delivery saved");
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="delivery-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "支票领取 / 通知分包商" : "Check delivery / notify"}</DialogTitle>
          <DialogDescription>{r.tradeName} · {r.milestoneName} · {money(r.amount)}{r.checkNo ? ` · #${r.checkNo}` : ""}</DialogDescription>
        </DialogHeader>
        <DeliveryPicker r={r} zh={zh} api={api} value={v} onChange={setV} testid="delivery-dlg" />
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy} onClick={submit} data-testid="delivery-dlg-submit" className="bg-brand-500 hover:bg-brand-600 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "确认并通知" : "Confirm & notify")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* 管理员 / CEO：修正过往支付数据（付款日期 · 支票号 · 金额 · 备注 + 原因） */
export function AdminEditDialog({ r, zh, api, onClose, onDone }) {
  const [f, setF] = useState({ paidAt: (r.paidAt || "").slice(0, 10), checkNo: r.checkNo || "", amount: r.amount || "", note: r.note || "", reason: "" });
  const [busy, setBusy] = useState(false);
  const inp = "w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm";
  const submit = async () => {
    if (!f.reason.trim()) return toast.error(zh ? "请填写修正原因" : "Reason required");
    // 用户本轮重开票据审查：金额和票号已固定于旧新票链，备注/日期修正不能携带未改的锁定字段。
    const reissued = !!r.checkReissues?.length;
    if (reissued && (Number(f.amount) !== Number(r.amount) || String(f.checkNo) !== String(r.checkNo || ""))) {
      return toast.error(zh ? "重开支票的金额和支票号不可修改，请保留原值后保存日期或备注" : "Reissued check amount and number are locked. Restore them before saving date or note changes.");
    }
    const body = { paidAt: f.paidAt || undefined, note: f.note, reason: f.reason.trim() };
    if (!reissued) Object.assign(body, { checkNo: f.checkNo, amount: Number(f.amount) || undefined });
    setBusy(true);
    try {
      await api.patch(`/payment-requests/${r.id}/admin-edit`, body);
      toast.success(zh ? "已修正并留痕" : "Updated");
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4" />{zh ? "修正支付记录（管理员）" : "Edit payment record (admin)"}</DialogTitle>
          <DialogDescription>{r.tradeName} · {r.milestoneName} · {zh ? "每次修正都会写入审批历史与审计日志，并同步账单 / 支票 / 阶段" : "Every edit is logged and synced to bill / check / milestone"}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-600">
          <label>{zh ? "付款日期" : "Paid date"}<input type="date" value={f.paidAt} onChange={(e) => setF({ ...f, paidAt: e.target.value })} className={inp} data-testid="admin-edit-paidat" /></label>
          <label>{zh ? "支票号" : "Check #"}<input value={f.checkNo} onChange={(e) => setF({ ...f, checkNo: e.target.value })} className={inp} data-testid="admin-edit-checkno" /></label>
          <label>{zh ? "金额" : "Amount"}<input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inp} data-testid="admin-edit-amount" /></label>
          <label className="sm:col-span-2">{zh ? "备注" : "Note"}<input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inp} data-testid="admin-edit-note" /></label>
          <label className="sm:col-span-2">{zh ? "修正原因（必填）" : "Reason (required)"}<input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={inp} placeholder={zh ? "例：支票号登记错误，实际为 #4002" : "e.g. wrong check number"} data-testid="admin-edit-reason" /></label>
        </div>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy} onClick={submit} data-testid="admin-edit-submit" className="bg-brand-500 hover:bg-brand-600 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "保存修正" : "Save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* 财务 / 出纳 / 工程经理 / 管理员：作废已支付记录（记录保留 + 支票作废 + 账单退回未付 + 节点退回待发起 + Lien Waiver 作废）
   需求（用户 2026-10-22）：项目 1636 保温隔热第一张支票没有供应商公司名，只能另开一张，同一笔 $3,240
   变成两条「已支付」。原来只有管理员的「删除」（记录直接消失），财务和工程经理都无法自助纠正，
   也看不出「曾经付过又作废」，所以新增作废入口：状态标 void，全链路留痕，可重新发起付款。 */
export function PaymentVoidDialog({ r, zh, api, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notifySub, setNotifySub] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error(zh ? "请填写作废原因" : "Reason required");
    setBusy(true);
    try {
      const { data } = await api.post(`/payment-requests/${r.id}/void`, { reason: reason.trim(), notify: notifySub ? { sms: true, email: true } : undefined });
      const parts = [zh ? "付款记录已作废" : "Payment voided"];
      if (data?.voidedCheck) parts.push(zh ? "支票已作废" : "check voided");
      if (data?.billsReset) parts.push(zh ? "账单已退回未付" : "bill reopened");
      if (data?.voidedLien) parts.push(zh ? `Lien Waiver ${data.voidedLien.docNo} 已作废` : `lien ${data.voidedLien.docNo} voided`);
      parts.push(zh ? "该付款节点已退回「待发起」，可重新发起付款" : "milestone released");
      toast.success(parts.join(" · "));
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="payment-void-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="h-4 w-4" />{zh ? "作废已支付的付款记录" : "Void a paid record"}</DialogTitle>
          <DialogDescription>
            {r.tradeName} · {r.milestoneName} · <b className="text-red-600">{money(r.amount)}</b>{r.checkNo ? ` · ${zh ? "支票" : "check"} #${r.checkNo}` : ""}
            <br />{zh ? "记录会保留并标记「已作废」，不会消失。同时：作废关联支票、账单退回未付、该付款节点退回「待发起」（可重新付款）；若已自动生成 Lien Waiver 也一并作废。" : "The record is kept and marked void. The check is voided, the bill reopens, the milestone is released, and any auto-generated lien waiver is voided."}
          </DialogDescription>
        </DialogHeader>
        <label className="block text-xs text-slate-600" data-testid="payment-void-reason-label">
          {zh ? "作废原因（必填，写入审批历史与审计）" : "Reason (required, recorded)"}
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} data-testid="payment-void-reason"
            placeholder={zh ? "例：支票未印供应商公司名，已重开一张，此笔为重复记录" : "e.g. check issued without the vendor company name; reissued"} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="payment-void-notify-label">
          <input type="checkbox" checked={notifySub} onChange={(e) => setNotifySub(e.target.checked)} className="mt-0.5 rounded border-slate-300" />
          <span>{zh ? "同时通知分包商这笔付款已作废（短信 + 邮件，防止其继续兑付该支票）" : "Also notify the subcontractor (SMS + email) that this payment was voided"}</span>
        </label>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy || !reason.trim()} onClick={submit} data-testid="payment-void-submit" className="bg-red-600 hover:bg-red-700 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "确认作废" : "Void payment")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* 管理员 / CEO：删除支付记录（作废支票 · 删账单 · 阶段退回） */
export function AdminDeleteDialog({ r, zh, api, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error(zh ? "请填写删除原因" : "Reason required");
    setBusy(true);
    try {
      await api.delete(`/payment-requests/${r.id}`, { params: { reason: reason.trim() } });
      toast.success(zh ? "支付记录已删除：支票已作废、账单已删除、阶段退回「计划中」" : "Deleted");
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="h-4 w-4" />{zh ? "删除支付记录（管理员）" : "Delete payment record (admin)"}</DialogTitle>
          <DialogDescription>{r.tradeName} · {r.milestoneName} · <b className="text-red-600">{money(r.amount)}</b>{r.checkNo ? ` · #${r.checkNo}` : ""}<br />{zh ? "将同时：作废关联支票、删除关联账单与凭证归档、该付款阶段退回「计划中」；原记录快照保留在审计。" : "Voids the check, removes the bill & vouchers, releases the milestone; snapshot kept for audit."}</DialogDescription>
        </DialogHeader>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={zh ? "删除原因（必填）" : "Reason (required)"} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="admin-delete-reason" />
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy} onClick={submit} data-testid="admin-delete-submit" className="bg-red-600 hover:bg-red-700 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "确认删除" : "Delete")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** 用户本轮提出沿用原工程审批重开支票：旧票须受控，金额固定，新票抬头可纠正。
 * 本操作只生成待打印票；仍须通过原有打印确认完成付款，不能借重开改金额或重复验收。 */
export function CheckReissueDialog({ r, zh, api, onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ controlMethod: "", controlDate: "", controlReference: "", reason: "", payeeName: r.checkPayee || r.check?.payee || r.check?.payeeName || r.subcontractorName || "", bankAccountId: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useRef(null);
  const set = (key, value) => setForm((v) => ({ ...v, [key]: value }));
  // 同银行可能有多个出票账户；只展示服务端返回的脱敏尾号，不读取或拼接完整账号。
  const accountLabel = (a) => {
    const parts = [a.bankName, a.companyName, a.accountName || a.name || a.id];
    const last4 = String(a.accountLast4 || "").slice(-4);
    return [...parts.filter((part, index) => part && parts.indexOf(part) === index), ...(last4 ? [`•••• ${last4}`] : [])].join(" · ");
  };
  useEffect(() => {
    let current = true;
    // 重开权限单独配置，工程同事无需额外取得财务账户列表权限。
    api.get(`/payment-requests/${r.id}/reissue-accounts`).then(({ data }) => {
      // 后端按原项目推导分公司并仅返回可用账户；旧付款申请可能没有 branch 字段，前端不能再次按该字段过滤。
      const list = Array.isArray(data) ? data : [];
      if (current) { setAccounts(list); set("bankAccountId", list.find((a) => a.id === r.bankAccountId)?.id || list[0]?.id || ""); }
    }).catch(() => { if (current) setError(zh ? "付款账户加载失败，请关闭后重试" : "Unable to load bank accounts. Close and retry."); });
    return () => { current = false; };
  }, [api, r.id, r.bankAccountId, zh]);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
    if (Object.values(body).some((v) => !v)) { setError(zh ? "请填写旧票控制方式、日期、凭据编号、重开原因、收款人和付款账户" : "Complete the old check control evidence, reason, payee and bank account."); return; }
    setError(""); setBusy(true);
    try {
      await api.post(`/payment-requests/${r.id}/reissue-check`, body);
      toast.success(zh ? "旧票已作废，新票待打印；原付款审批继续有效" : "Old check voided; replacement awaits printing. Original approval retained.");
      onDone();
    } catch (err) { setError(err?.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  const inputClass = "mt-1 w-full rounded-lg border border-slate-300 bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400";
  return <Dialog open onOpenChange={() => { if (!busy) onClose(); }}>
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" closeDisabled={busy} data-testid="check-reissue-dialog">
      <DialogHeader><DialogTitle>{zh ? "作废旧支票并重开" : "Void old check and reissue"}</DialogTitle><DialogDescription>{zh ? "保留原付款申请、账单和工程审批，无须重复验收。新票生成后须另行打印确认。" : "Retain the original payment request, bill and engineering approval. Print and confirm the replacement separately."}</DialogDescription></DialogHeader>
      <form onSubmit={submit} noValidate className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1 break-words">
          <p>{zh ? "旧支票" : "Old check"} #{r.checkNo || r.checkId}</p>
          <p>{zh ? "原付款申请" : "Original request"}：{r.id}</p>
          <p>{zh ? "原账单" : "Original bill"}：{r.billId || r.bill?.id || (zh ? "沿用原关联账单" : "Retained linked bill")}</p>
          <p>{zh ? "工程审批" : "Engineering approval"}：{r.engReview?.by || r.engReviewedBy || r.engApprovedBy || (zh ? "沿用原审批记录" : "Original approval retained")}</p>
          <p data-testid="reissue-amount" className="font-semibold">{zh ? "固定金额" : "Fixed amount"}：{money(r.amount)}</p>
        </div>
        <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3">{zh ? "确认旧纸票已收回并销毁，或银行已确认止付后才能重开。旧票保留作废记录，不再作为有效付款票据。" : "Reissue only after recovering and destroying the old paper check or receiving confirmed bank stop payment. The old check remains in the void history."}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">{zh ? "新票收款人" : "Replacement payee"}<Input data-testid="reissue-payee" className={inputClass} value={form.payeeName} onChange={(e) => set("payeeName", e.target.value)} disabled={busy} /></label>
          <label className="text-sm">{zh ? "付款账户（原分公司）" : "Bank account (original branch)"}<select data-testid="reissue-account" className={inputClass} value={form.bankAccountId} onChange={(e) => set("bankAccountId", e.target.value)} disabled={busy}><option value="">{zh ? "请选择账户" : "Select account"}</option>{accounts.map((a) => <option key={a.id} value={a.id}>{accountLabel(a)}</option>)}</select></label>
          <label className="text-sm">{zh ? "旧票控制方式" : "Old check control"}<select data-testid="reissue-method" className={inputClass} value={form.controlMethod} onChange={(e) => set("controlMethod", e.target.value)} disabled={busy}><option value="">{zh ? "请选择证明方式" : "Select evidence"}</option><option value="recovered_destroyed">{zh ? "纸票已收回并销毁" : "Recovered and destroyed"}</option><option value="stop_payment">{zh ? "银行已确认止付" : "Bank stop payment confirmed"}</option></select></label>
          <label className="text-sm">{zh ? "收回销毁 / 止付确认日期" : "Control confirmation date"}<Input type="date" data-testid="reissue-date" className={inputClass} value={form.controlDate} onChange={(e) => set("controlDate", e.target.value)} disabled={busy} /></label>
        </div>
        <label className="block text-sm">{zh ? "销毁记录 / 银行止付凭据编号" : "Destruction record / bank confirmation reference"}<Input data-testid="reissue-reference" className={inputClass} value={form.controlReference} onChange={(e) => set("controlReference", e.target.value)} disabled={busy} /></label>
        <label className="block text-sm">{zh ? "重开原因" : "Reason for reissue"}<Input data-testid="reissue-reason" className={inputClass} value={form.reason} onChange={(e) => set("reason", e.target.value)} disabled={busy} /></label>
        {error && <p role="alert" tabIndex={-1} ref={errorRef} className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose} disabled={busy}>{zh ? "取消" : "Cancel"}</Button><Button type="submit" data-testid="reissue-confirm" disabled={busy || !accounts.length}>{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{zh ? "确认旧票受控并生成新票" : "Confirm evidence & create replacement"}</Button></div>
      </form>
    </DialogContent>
  </Dialog>;
}
